// internal-deps
import { CheckoutMetaInfoResponsePayload } from "@swiggy-private/native-bridge";
import { Err, None, Ok, Option, Some } from "@swiggy-private/js-fp";
import { MARKETPLACE_BUSINESS_LINE } from "@swiggy-private/payment-core";
import {
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";

// interfaces
import { ResultGetPaymentMethods, FetchPaymentsAndMetaProps } from "@payment-client/interfaces/ShowPayments";

// apis
import { getPaymentMethods } from "@payment-client/apis/Payment";
import { getPaymentMethodsPublic } from "@payment-client/apis/publicPayment";

// analytics
import { onUnableToFetchPaymentMethodEvent } from "@payment-client/analytics/events/Payment";

// helpers
import { fetchCredElegiblity } from "@payment-client/helpers/CredHelper";
import { isInAppUpiEnabled } from "@payment-client/helpers/InAppUpiHelper";
import { getMerchantId } from "@payment-client/helpers/JuspayHelpers";
import { isOriginalBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { isEmpty } from "@swiggy-private/js-utils";
import { clearTurboPayFallbackConfig, saveTurboPayFallbackConfig } from "@payment-client/helpers/TurboPayment";

// constants
import { GIFT_DETAILS_STORAGE_KEY } from "@payment-client/constants/StorageKeys";

export async function fetchPaymentsAndMeta({
    apiHost,
    fetchCheckoutMetaInfo,
    impressionEvent,
    isRefetch,
    dataInCache,
    isUserEligibleForSingleClick,
    splitPayMethods,
    onRefetchErrCb,
    turboVariant,
    publicApiConfig,
    isBillToCompanySelected,
    billToCompanyInfoMeta,
    turboPayUniqueId,
}: FetchPaymentsAndMetaProps): Promise<[ResultGetPaymentMethods, Option<CheckoutMetaInfoResponsePayload>]> {
    // Fetching cred Elegiblity from cache
    const cachedPaymentResponse = dataInCache?.paymentResponse;
    const tenant = cachedPaymentResponse?.tenant;
    const cachedMerchantId = cachedPaymentResponse ? getMerchantId(cachedPaymentResponse) : undefined;
    const isUserCredEligible = await fetchCredElegiblity(tenant, cachedMerchantId);
    const isInAppUpiEligible = await isInAppUpiEnabled(tenant);
    let isGiftingFlow = false;

    // TODO - Cleanup required after feature is over
    if (isOriginalBusinessLineSwiggyMoney()) {
        let giftDetails;

        try {
            // Setting this session storage key gift-details in swiggy money IW
            giftDetails = JSON.parse(sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY) as string);
            if (giftDetails) {
                isGiftingFlow = true;
            }
        } catch {
            void 0;
        }
    }

    const isPublicApiEnable = isPublicApiEnabled(
        publicApiConfig || {},
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYMENT_APIS,
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.GPO_PAYMENT_APIS,
    );
    const getPaymentMethodsFunction = isPublicApiEnable ? getPaymentMethodsPublic : getPaymentMethods;
    const paymentPromise = getPaymentMethodsFunction(apiHost, {
        isUserCredEligible,
        splitPayMethods,
        isUserEligibleForSingleClick,
        isGiftingFlow,
        isInAppUpiEligible,
        turboVariant,
        isBillToCompanySelected,
        billToCompanyInfoMeta,
    })
        .then((data) => {
            if (!data) {
                if (isRefetch && dataInCache) {
                    onRefetchErrCb?.();
                    clearTurboPayFallbackConfig();
                    return Ok(dataInCache.paymentResponse);
                }
                throw new Error("Received empty data from get payment methods");
            }

            // clear turbo pay fallback config after successful payment methods fetch
            clearTurboPayFallbackConfig();
            return Ok(data);
        })
        .catch((err) => {
            onUnableToFetchPaymentMethodEvent(
                err.statusCode ?? 1,
                err.statusMessage ?? "Recieved error response from get payment methods",
                impressionEvent,
            );

            // save turbo pay unique id in case GPO failed to avoid calling GPO again for the same id
            if (!isEmpty(turboPayUniqueId)) {
                saveTurboPayFallbackConfig(turboPayUniqueId);
            }

            if (isRefetch && dataInCache) {
                onRefetchErrCb?.();
                return Ok(dataInCache.paymentResponse);
            }
            return Err(err) as ResultGetPaymentMethods;
        });

    const businessCategory = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory);
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine);

    const checkoutMetaPromise = !fetchCheckoutMetaInfo
        ? Promise.resolve(None)
        : getBridgeSdk()
              .getCheckoutMetaInfo({ businessCategory, businessLine })
              .then((checkoutMetaInfo) => {
                  if (businessLine === MARKETPLACE_BUSINESS_LINE.REGULAR && checkoutMetaInfo?.deliveryAddress == null) {
                      recordNREvent({
                          eventType: NEWRELIC_TABLES.INVALID_CHECKOUT_META,
                          cartItemsCount: String(checkoutMetaInfo?.cartItemsCount),
                      });
                  }
                  return !fetchCheckoutMetaInfo ? None : Some(checkoutMetaInfo);
              })
              .catch((err) => {
                  if (__DEV__) {
                      // eslint-disable-next-line no-console
                      console.error(err);
                  }
                  return None as Option<CheckoutMetaInfoResponsePayload>;
              });
    return Promise.all([paymentPromise, checkoutMetaPromise]);
}
